import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { X, Send, Trash2, Loader2, Calendar, HelpCircle } from "lucide-react";
import { ErnieAvatar } from "@/components/app/ErnieAvatar";
import { ChatMarkdown, ChatLinks, splitLinks } from "@/components/app/ChatMarkdown";

const MODES = {
  fixtures: {
    label: "Fixtures",
    icon: Calendar,
    endpoint: "/ai/fixtures-chat",
    storageKey: "eyfc:fixtures-chat-session",
    placeholder: "Ask about a fixture…",
    subtitle: "Ernie · live booking data",
    intro: "Hi, I'm Ernie. Ask me anything about the club's fixtures — training, games, referees, dates. I answer from the live booking calendar.",
    suggestions: [
      "When's my next home game?",
      "Any U12 fixtures this weekend?",
      "Who's reffing on Sunday?",
      "What's on next Saturday?",
    ],
  },
  helper: {
    label: "How do I…",
    icon: HelpCircle,
    endpoint: "/ai/coach-helper",
    storageKey: "eyfc:coach-helper-session",
    placeholder: "e.g. how do I submit a kit order?",
    subtitle: "Ernie · app how-to & club docs",
    intro: "Hi, I'm Ernie. Ask me anything about using the app or the club's documents — booking a pitch, kit orders, policies, league rules.",
    suggestions: [
      "What do the league rules say about match length?",
      "How do I submit a kit order?",
      "How do I book a pitch?",
      "How do I add a team?",
      "How do I make someone a Kit Admin?",
    ],
  },
};

export default function FixturesChat() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("fixtures");
  const cfg = MODES[mode];

  // Per-mode session id (stored separately so switching modes doesn't cross conversations)
  const [sessionMap, setSessionMap] = useState(() => ({
    fixtures: localStorage.getItem(MODES.fixtures.storageKey) || "",
    helper: localStorage.getItem(MODES.helper.storageKey) || "",
  }));
  const sessionId = sessionMap[mode] || "";
  const setSessionId = (id) => {
    setSessionMap(prev => ({ ...prev, [mode]: id }));
    if (id) localStorage.setItem(cfg.storageKey, id); else localStorage.removeItem(cfg.storageKey);
  };

  const [messagesMap, setMessagesMap] = useState({ fixtures: [], helper: [] });
  const messages = messagesMap[mode];
  const setMessages = (updater) => setMessagesMap(prev => ({
    ...prev,
    [mode]: typeof updater === "function" ? updater(prev[mode]) : updater,
  }));

  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollerRef = useRef(null);

  // Load prior history for the current mode when opened / mode changes
  useEffect(() => {
    if (!open || !sessionId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`${cfg.endpoint}/${sessionId}`);
        if (!cancelled) setMessages(r.data.messages || []);
      } catch (err) { console.warn(`${mode} chat history load failed:`, err); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sessionId, mode]);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    const optimistic = { id: `tmp-${Date.now()}`, role: "user", text: msg, at: new Date().toISOString() };
    setMessages(prev => [...prev, optimistic]);
    setSending(true);
    try {
      const r = await api.post(cfg.endpoint, { session_id: sessionId || null, message: msg });
      if (r.data.session_id && r.data.session_id !== sessionId) setSessionId(r.data.session_id);
      setMessages(prev => [...prev, { id: `a-${Date.now()}`, role: "assistant", text: r.data.reply, at: new Date().toISOString() }]);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setMessages(prev => [...prev, { id: `e-${Date.now()}`, role: "assistant", text: typeof detail === "string" ? detail : "Something went wrong. Please try again.", at: new Date().toISOString() }]);
    } finally {
      setSending(false);
    }
  };

  const clear = async () => {
    if (!sessionId) { setMessages([]); return; }
    try { await api.delete(`${cfg.endpoint}/${sessionId}`); } catch (err) { console.warn("clear failed:", err); }
    setMessages([]);
    setSessionId("");
  };

  const ModeIcon = cfg.icon;

  return (
    <>
      <button
        onClick={() => setOpen(v => !v)}
        data-testid="fixtures-chat-toggle"
        aria-label={open ? "Close chat" : "Open chat"}
        className="fixed bottom-24 md:bottom-6 right-4 md:right-6 z-40 w-14 h-14 rounded-full bg-[#83B68B] text-white shadow-lg shadow-[#83B68B]/20 hover:scale-105 active:scale-95 transition-transform flex items-center justify-center"
      >
        {open ? <X className="w-6 h-6" /> : <ErnieAvatar size={52} />}
      </button>

      {open && (
        <div
          data-testid="fixtures-chat-panel"
          className="fixed bottom-[10.5rem] md:bottom-24 right-4 md:right-6 z-40 w-[calc(100vw-2rem)] sm:w-96 h-[32rem] max-h-[calc(100dvh-13rem)] md:max-h-[calc(100vh-8rem)] rounded-lg border border-[#83B68B]/30 bg-[#0A0F1C] text-white shadow-2xl shadow-black/50 flex flex-col overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2 bg-[#0F172A]">
            <ErnieAvatar size={40} wave className="shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-display font-black text-sm tracking-tight" data-testid="ai-chat-title">Ask Ernie</div>
              <div className="text-[10px] tracking-widest uppercase text-white/50">{cfg.subtitle}</div>
            </div>
            {messages.length > 0 && (
              <button onClick={clear} data-testid="fixtures-chat-clear" aria-label="Clear conversation" className="text-white/40 hover:text-white/80 p-1">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Mode toggle */}
          <div className="px-3 pt-3 pb-2 border-b border-white/5 bg-[#0F172A]/60">
            <div className="grid grid-cols-2 gap-1 p-1 bg-black/30 rounded">
              {Object.entries(MODES).map(([k, m]) => {
                const active = k === mode;
                const Icon = m.icon;
                return (
                  <button
                    key={k}
                    onClick={() => setMode(k)}
                    data-testid={`chat-mode-${k}`}
                    className={`text-xs tracking-widest uppercase px-2 py-1.5 rounded flex items-center justify-center gap-1.5 transition-colors ${
                      active ? "bg-[#83B68B] text-white font-semibold" : "text-white/60 hover:text-white/90"
                    }`}
                  >
                    <Icon className="w-3 h-3" />
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div ref={scrollerRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="fixtures-chat-log">
            {messages.length === 0 ? (
              <div className="text-sm text-white/60">
                <p className="mb-3">{cfg.intro}</p>
                <div className="grid gap-2">
                  {cfg.suggestions.map((s, i) => (
                    <button
                      key={`${mode}-${s}`}
                      onClick={() => send(s)}
                      data-testid={`fixtures-chat-suggestion-${i}`}
                      className="text-left text-xs px-3 py-2 rounded border border-white/10 hover:border-[#83B68B]/60 hover:bg-white/5 transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map(m => {
                const { text, links } = m.role === "user" ? { text: m.text, links: [] } : splitLinks(m.text);
                return (
                <div key={m.id} className={`flex items-end gap-1.5 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  {m.role !== "user" && <ErnieAvatar size={22} className="shrink-0 mb-0.5" />}
                  <div
                    data-testid={`fixtures-chat-msg-${m.role}`}
                    className={`max-w-[85%] px-3 py-2 rounded-lg text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-[#83B68B] text-white rounded-br-sm whitespace-pre-wrap"
                        : "bg-white/5 text-white/90 border border-white/10 rounded-bl-sm"
                    }`}
                  >
                    {m.role === "user" ? text : <ChatMarkdown text={text} />}
                    <ChatLinks links={links} onNavigate={() => setOpen(false)} />
                  </div>
                </div>
                );
              })
            )}
            {sending && (
              <div className="flex justify-start">
                <div className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 flex items-center gap-2 text-xs text-white/60">
                  <Loader2 className="w-3 h-3 animate-spin text-[#83B68B]" />
                  Thinking…
                </div>
              </div>
            )}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="p-3 border-t border-white/10 flex items-center gap-2 bg-[#0F172A]"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={cfg.placeholder}
              maxLength={1000}
              data-testid="fixtures-chat-input"
              disabled={sending}
              className="flex-1 bg-black/30 border border-white/10 focus:border-[#83B68B] rounded px-3 py-2 text-sm outline-none disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              data-testid="fixtures-chat-send"
              aria-label="Send"
              className="w-9 h-9 flex items-center justify-center rounded bg-[#83B68B] text-white disabled:opacity-40 disabled:cursor-not-allowed hover:scale-105 active:scale-95 transition-transform"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
